import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { AlertService } from 'src/app/alerts/alerts.service';
import { ComponentsModule } from 'src/app/components/components.module';
import type { CompanyFeesConfig } from 'src/app/configuration/companies/companies-config/companies-class';
import { OriginsEnum } from 'src/app/configuration/enums/origins.enum';
import { PaymentModulesEnum } from 'src/app/configuration/payment-modules/payment-module-class';
import { OrderState } from 'src/app/features/checkout/states/order.state';
import { PaymentState } from 'src/app/features/checkout/states/payment.state';
import { PaymentConditions } from 'src/app/models/create-order';
import { FeesDetailItem } from 'src/app/models/fee.model';
import { PaymentTypeNamesEnum } from 'src/app/models/payments.model';
import { InstallmentsService } from 'src/app/services/installments.service';
import {
  selectCompanyFeesConfig,
  selectCompanyshowSelfFees,
} from 'src/app/store/states/company/company.selectors';
import { selectConfigOrigin } from 'src/app/store/states/config/config.selectors';
import {
  selectPaymentModule,
  selectPaymentOrder,
} from 'src/app/store/states/payment/payment.selectors';

@Component({
  selector: 'app-fees-detail',
  templateUrl: './fees-detail.component.html',
  styleUrls: ['./fees-detail.component.scss'],
  imports: [CommonModule, ComponentsModule],
})
export class FeesDetailComponent {
  private store = inject(Store);
  protected alertService = inject(AlertService);
  protected isFixPaymentCondition = computed(
    () =>
      this.store.selectSignal(selectPaymentModule)() == PaymentModulesEnum.FIXED_PAYMENT_CONDITIONS
  );
  protected originsEnum = OriginsEnum;
  protected origin = this.store.selectSignal(selectConfigOrigin);
  private orderState = inject(OrderState);
  private installmentService = inject(InstallmentsService);
  private readonly paymentType = inject(PaymentState).paymentType;
  protected companyFeesConfig = this.store.selectSignal(selectCompanyFeesConfig);

  protected fees = computed(() =>
    this.buildFeesDetailItems(
      this.paymentType() as PaymentTypeNamesEnum,
      this.orderState.paymentConditions(),
      this.installmentService.selectedInstallment()?.installment,
      this.companyFeesConfig()
    )
  );

  private buildFeesDetailItems(
    paymentType: PaymentTypeNamesEnum,
    paymentCondition: PaymentConditions | null,
    selectedInstallment: number | undefined,
    feesConfig: CompanyFeesConfig
  ): FeesDetailItem[] {
    const shouldBuildCreditFeeItems =
      paymentType === PaymentTypeNamesEnum.CREDIT ||
      paymentType === PaymentTypeNamesEnum.MULTIPLE_CARDS;

    if (shouldBuildCreditFeeItems) {
      return this.buildCreditFeesDetailItems(paymentCondition, selectedInstallment, feesConfig);
    }

    return this.buildDebitFeeItems(paymentCondition);
  }

  private buildCreditFeesDetailItems(
    paymentCondition: PaymentConditions | null,
    selectedInstallment: number | undefined,
    feesConfig: CompanyFeesConfig
  ): FeesDetailItem[] {
    const installment = selectedInstallment ?? 1;
    const isCreditAvista = installment === 1;
    const feeItems: FeesDetailItem[] = [
      {
        fee: `${paymentCondition?.credit.fees[1]}%`,
        message: 'Crédito à Vista',
        valid: !!paymentCondition?.credit.fees[1],
      },
    ];

    if (!isCreditAvista) {
      const feeItem = this.buildCreditInstallmentFeeItem(feesConfig, paymentCondition, installment);
      feeItems.push(feeItem);
    }
    return feeItems;
  }

  private buildCreditInstallmentFeeItem(
    feesConfig: CompanyFeesConfig,
    paymentCondition: PaymentConditions | null,
    selectedInstallment: number
  ): FeesDetailItem {
    if (feesConfig.usesFixedFee) {
      return {
        fee: `${feesConfig.fixedFeePercent}% ${feesConfig.installmentFeeDisplayUnit}`,
        message: 'Crédito Parcelado',
        valid: !!feesConfig.fixedFeePercent,
      };
    }
    return {
      fee: `${paymentCondition?.credit.fees[selectedInstallment]}% ${feesConfig.installmentFeeDisplayUnit}`,
      message: 'Crédito Parcelado',
      valid: !!paymentCondition?.credit.fees[selectedInstallment],
    };
  }

  private buildDebitFeeItems(paymentCondition: PaymentConditions | null): FeesDetailItem[] {
    return [
      {
        fee: paymentCondition?.debit.fees[1],
        message: 'Débito cartão virtual Caixa',
        valid: !!paymentCondition?.debit.fees[1],
      },
    ];
  }
  protected showSelfFees = this.store.selectSignal(selectCompanyshowSelfFees);
  protected getfee = computed(() => {
    return this.store.selectSignal(selectPaymentOrder)().paymentConditionsInfo?.fee;
  });
}
